/**
 * Previews for files that can never have a picture.
 *
 * A spreadsheet and a timeseries export are not rasterisable: the indexer only
 * converts PDF/DOCX/PPTX/PNG/JPEG/TXT into page PDFs, so no thumbnail is ever
 * written for them and `/v1/index/:id/thumbnail` raises. These cards draw a
 * small grid instead, so the card reads as "a spreadsheet" rather than as a
 * broken preview.
 *
 * Given a `docId`, the grid holds the file's OWN first rows, read from the
 * preview ingest saved (see useSheetPreviewHead). Without one, or while that
 * loads, or when the file has none, the grid is empty. It never shows made-up
 * values: it used to fill every timeseries card with the same fictional rows
 * ("FT-101 … 2024-01-01"), which read as the file's data (Slack, 2026-10-12:
 * "the fabricated TS view bug").
 *
 * Lifted out of DocumentPreviewShape/DocumentIcon.tsx so the tag-mapping board
 * can show the SAME thing. Importing them from there is not an option: that
 * module pulls in `pdfjs-dist`, which would drag pdf.js into any chunk that
 * wanted a table.
 */
import { memo } from 'react'
import type { SheetPreviewHead } from '@/service/api'
import { useSheetPreviewHead } from './useSheetPreviewHead'
import { formatCell } from './formatCell'

// Rows an empty grid draws: about what fills the card at 7px.
const EMPTY_ROWS = 10


type PreviewState = 'data' | 'loading' | 'empty'
const stateOf = (head: SheetPreviewHead | null, loading: boolean): PreviewState =>
  head && head.columns.length > 0 ? 'data' : loading ? 'loading' : 'empty'

const GRID = 'border border-gray-200 dark:border-dark-border'
const GUTTER = `${GRID} bg-gray-50 dark:bg-dark-muted px-1 py-0.5 text-center text-gray-400 dark:text-gray-500`
const colLetter = (i: number) => String.fromCharCode(65 + (i % 26))

export const SpreadsheetCard = memo(({ docId }: { docId?: string }) => {
  const { head, loading } = useSheetPreviewHead(docId)
  const state = stateOf(head, loading)
  const header = state === 'data' && head ? head.columns : ['', '', '', '', '']
  const body = state === 'data' && head ? head.rows : Array.from({ length: EMPTY_ROWS - 1 }, () => header.map(() => ''))
  return (
    <div className="w-full h-full self-stretch overflow-hidden" data-preview-state={state}>
      <table className="w-full h-full border-collapse text-[7px] leading-[10px] text-gray-600 dark:text-gray-300">
        <thead>
          <tr>
            <th className={`${GUTTER} font-normal w-[16px]`} />
            {header.map((_, ci) => (
              <th key={ci} className={`${GUTTER} font-semibold`}>
                {colLetter(ci)}
              </th>
            ))}
          </tr>
          <tr>
            <td className={GUTTER}>1</td>
            {header.map((cell, ci) => (
              <td key={ci} className={`${GRID} bg-gray-100 dark:bg-dark-accent px-1 py-0.5 font-semibold truncate max-w-[60px]`}>
                {cell}
              </td>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, ri) => (
            <tr key={ri}>
              <td className={GUTTER}>{ri + 2}</td>
              {header.map((_, ci) => (
                <td key={ci} className={`${GRID} px-1 py-0.5 truncate max-w-[60px]`}>
                  {formatCell(row[ci])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
})
SpreadsheetCard.displayName = 'SpreadsheetCard'

export const TimeseriesCard = memo(({ docId }: { docId?: string }) => {
  const { head, loading } = useSheetPreviewHead(docId)
  const state = stateOf(head, loading)
  const header = state === 'data' && head ? head.columns : ['', '', '', '']
  const body = state === 'data' && head ? head.rows : Array.from({ length: EMPTY_ROWS }, () => header.map(() => ''))
  return (
    <div className="w-full h-full self-stretch overflow-hidden" data-preview-state={state}>
      <table className="w-full h-full border-collapse text-[7px] leading-[10px] text-gray-600 dark:text-gray-300">
        <thead>
          <tr>
            {header.map((col, ci) => (
              <th key={ci} className={`${GRID} bg-gray-100 dark:bg-dark-accent px-1 py-0.5 text-left font-semibold truncate max-w-[60px]`}>
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, ri) => (
            <tr key={ri}>
              {header.map((_, ci) => (
                <td key={ci} className={`${GRID} px-1 py-0.5 truncate max-w-[60px]`}>
                  {formatCell(row[ci])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
})
TimeseriesCard.displayName = 'TimeseriesCard'
